import React, { useState, useEffect, useRef } from "react";

import { EdiPaymentLine } from "../types";
import { formatDrgFromFields } from "../drgDisplay";
import { formatDisplayDate } from "../../../helpers/dataHelper";
import {
  formatCurrency,
  formatEntityName,
  getAllowedFromSupplemental,
  getModifiersFromLine,
  getLinePatientResponsibility,
} from "../viewerHelpers";
import DetailSection from "./DetailSection";
import HoverTooltip from "../../../ui/HoverTooltip";

const PaymentServiceLineRow: React.FC<{
  line: EdiPaymentLine;
  index: number;
  allExpanded?: boolean;
  showModifiersColumn?: boolean;
}> = ({ line, index, allExpanded, showModifiersColumn = true }) => {
  const [expanded, setExpanded] = useState(false);
  const [showCodeTooltip, setShowCodeTooltip] = useState(false);
  const codeRef = useRef<HTMLSpanElement>(null);

  // Sync with allExpanded prop
  useEffect(() => {
    if (allExpanded !== undefined) {
      setExpanded(allExpanded);
    }
  }, [allExpanded]);
  const hasAdjustments = line.adjustments && line.adjustments.length > 0;
  const hasRemarks =
    (line.remarkCodes && line.remarkCodes.length > 0) ||
    (line.remarks && line.remarks.length > 0);
  const hasServiceDateTo =
    line.serviceDateTo && line.serviceDateTo !== line.serviceDateFrom;
  const normalizedUnitType = (line.unitType || "").trim().toUpperCase();
  const showUnitType = !!(
    normalizedUnitType &&
    normalizedUnitType !== "UNIT" &&
    normalizedUnitType !== "UN"
  );
  const showOriginalUnits =
    line.originalUnitCount !== undefined &&
    line.originalUnitCount !== line.unitCount;
  const hasUnitInfo = showUnitType || showOriginalUnits;
  const hasOriginalCodes =
    line.originalProcedure || line.originalRevenueCode || line.originalDrug;
  const hasDrug = line.drug;
  const hasSupplementalAmounts =
    line.supplementalAmounts && line.supplementalAmounts.length > 0;
  const hasSupplementalQuantities =
    line.supplementalQuantities && line.supplementalQuantities.length > 0;
  const hasReferenceNumbers =
    line.repricedReferenceNumber ||
    line.adjustedRepricedReferenceNumber ||
    line.healthcarePolicyId;
  const hasServiceIds = line.serviceIds && line.serviceIds.length > 0;
  const hasProviders = line.providers && line.providers.length > 0;
  const hasRenderingProviderIds =
    line.renderingProviderIds && line.renderingProviderIds.length > 0;
  const lineDrg = formatDrgFromFields(line.drg, line.drgCode);
  const hasLineDrg = !!lineDrg;

  const hasDetails =
    hasAdjustments ||
    hasRemarks ||
    hasServiceDateTo ||
    hasUnitInfo ||
    hasOriginalCodes ||
    hasDrug ||
    hasSupplementalAmounts ||
    hasSupplementalQuantities ||
    hasReferenceNumbers ||
    hasServiceIds ||
    hasProviders ||
    hasRenderingProviderIds ||
    hasLineDrg;
  const hasExpandableContent = hasDetails;

  // Build a list of all denial codes in this line for DOM-based navigation
  const lineCodes: string[] = [];
  line.adjustments?.forEach((adj) => {
    const group = adj.group || "";
    const reason = adj.reason?.code || adj.reasonCode || "";
    if (reason) lineCodes.push(`${group}-${reason}`);
  });
  line.remarks?.forEach((r) => {
    if (r.code) lineCodes.push(r.code);
  });
  line.remarkCodes?.forEach((c) => {
    if (c) lineCodes.push(c);
  });

  return (
    <>
      <tr
        className={`border-b border-gray-100 hover:bg-gray-50 ${hasExpandableContent ? "cursor-pointer" : ""}`}
        onClick={() => hasExpandableContent && setExpanded(!expanded)}
        role={hasExpandableContent ? "button" : undefined}
        aria-expanded={hasExpandableContent ? expanded : undefined}
        tabIndex={hasExpandableContent ? 0 : undefined}
        data-line-index={index}
        data-codes={lineCodes.length > 0 ? lineCodes.join(",") : undefined}
      >
        <td className="px-3 py-2 text-sm">
          {hasExpandableContent && (
            <span
              className={`inline-block transition-transform ${expanded ? "rotate-90" : ""}`}
            >
              ▶
            </span>
          )}
        </td>
        <td className="px-3 py-2 text-md font-medium text-gray-900">
          {index + 1}
        </td>
        <td className="px-3 py-2">
          <div className="text-md font-medium text-gray-900 cursor-default">
            <span
              ref={codeRef}
              onMouseEnter={() => setShowCodeTooltip(true)}
              onMouseLeave={() => setShowCodeTooltip(false)}
            >
              {line.procedure?.code || "-"}
            </span>
            <HoverTooltip
              title={line.procedure?.desc || ""}
              isVisible={showCodeTooltip}
              triggerRef={codeRef}
            />
          </div>
        </td>
        {showModifiersColumn && (
          <td className="px-3 py-2">
            <div className="flex flex-wrap gap-1">
              {getModifiersFromLine(line).map((mod, i) => (
                <span
                  key={i}
                  className="px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded text-sm font-mono"
                >
                  {mod}
                </span>
              ))}
              {getModifiersFromLine(line).length === 0 && (
                <span className="text-gray-400">-</span>
              )}
            </div>
          </td>
        )}
        <td className="px-3 py-2 text-md text-gray-600 text-right">
          {line.unitCount ?? 1}
        </td>
        <td className="px-3 py-2 text-md text-gray-900 text-right font-medium">
          {formatCurrency(line.chargeAmount)}
        </td>
        <td className="px-3 py-2 text-md text-gray-600 text-right">
          {formatCurrency(
            line.allowedAmount ?? getAllowedFromSupplemental(line),
          )}
        </td>
        <td className="px-3 py-2 text-md text-green-600 text-right font-medium">
          {formatCurrency(line.paidAmount)}
        </td>
        <td className="px-3 py-2 text-md text-gray-600 text-right">
          {formatCurrency(getLinePatientResponsibility(line))}
        </td>
      </tr>
      {expanded && hasDetails && (
        <tr className="bg-gray-50">
          <td colSpan={8 + (showModifiersColumn ? 1 : 0)} className="px-4 py-2">
            <div className="space-y-2">
              {/* Service Dates */}
              {hasServiceDateTo && (
                <DetailSection title="Dates">
                  <div className="text-sm text-gray-700">
                    <span className="font-medium">From:</span>{" "}
                    {formatDisplayDate(line.serviceDateFrom)} |
                    <span className="font-medium ml-2">To:</span>{" "}
                    {formatDisplayDate(line.serviceDateTo)}
                  </div>
                </DetailSection>
              )}

              {/* Unit Information */}
              {hasUnitInfo && (
                <DetailSection title="Units">
                  <div className="flex gap-4 text-sm">
                    {showUnitType && (
                      <div>
                        <span className="text-gray-600">Type:</span>{" "}
                        <span className="font-medium">{line.unitType}</span>
                      </div>
                    )}
                    {showOriginalUnits && (
                      <div>
                        <span className="text-gray-600">Original:</span>{" "}
                        <span className="font-medium">
                          {line.originalUnitCount}
                        </span>
                        {line.unitCount !== undefined && (
                          <span className="text-gray-500 ml-1">
                            (Adj: {line.unitCount})
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </DetailSection>
              )}

              {/* Reference Numbers */}
              {hasReferenceNumbers && (
                <DetailSection title="Refs">
                  <div className="space-y-1 text-sm">
                    {line.healthcarePolicyId && (
                      <div>
                        <span className="text-gray-600">Policy ID (0K):</span>{" "}
                        <span className="font-mono ml-1">
                          {line.healthcarePolicyId}
                        </span>
                      </div>
                    )}
                    {line.repricedReferenceNumber && (
                      <div>
                        <span className="text-gray-600">Repriced (9B):</span>{" "}
                        <span className="font-mono ml-1">
                          {line.repricedReferenceNumber}
                        </span>
                      </div>
                    )}
                    {line.adjustedRepricedReferenceNumber && (
                      <div>
                        <span className="text-gray-600">
                          Adj Repriced (9D):
                        </span>{" "}
                        <span className="font-mono ml-1">
                          {line.adjustedRepricedReferenceNumber}
                        </span>
                      </div>
                    )}
                  </div>
                </DetailSection>
              )}

              {/* Original Codes (if different from adjudicated) */}
              {hasOriginalCodes && (
                <DetailSection title="Original">
                  <div className="space-y-2 text-sm">
                    {line.originalProcedure && (
                      <div className="border-l-2 border-blue-300 pl-2">
                        <div className="text-gray-600">Procedure:</div>
                        <div className="ml-2">
                          <span className="font-medium">Submitted:</span>{" "}
                          {line.originalProcedure.code || "-"}
                          {line.originalProcedure.desc && (
                            <span className="text-gray-500 ml-1">
                              ({line.originalProcedure.desc})
                            </span>
                          )}
                        </div>
                        {line.procedure && (
                          <div className="ml-2">
                            <span className="font-medium">Adjudicated:</span>{" "}
                            {line.procedure.code || "-"}
                            {line.procedure.desc && (
                              <span className="text-gray-500 ml-1">
                                ({line.procedure.desc})
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    {line.originalRevenueCode && (
                      <div className="border-l-2 border-blue-300 pl-2">
                        <div className="text-gray-600">Revenue Code:</div>
                        <div className="ml-2">
                          <span className="font-medium">Submitted:</span>{" "}
                          {line.originalRevenueCode.code || "-"}
                          {line.originalRevenueCode.desc && (
                            <span className="text-gray-500 ml-1">
                              ({line.originalRevenueCode.desc})
                            </span>
                          )}
                        </div>
                        {line.revenueCode && (
                          <div className="ml-2">
                            <span className="font-medium">Adjudicated:</span>{" "}
                            {line.revenueCode.code || "-"}
                            {line.revenueCode.desc && (
                              <span className="text-gray-500 ml-1">
                                ({line.revenueCode.desc})
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    {line.originalDrug && (
                      <div className="border-l-2 border-blue-300 pl-2">
                        <div className="text-gray-600">Drug (NDC):</div>
                        <div className="ml-2">
                          <span className="font-medium">Submitted:</span>{" "}
                          {line.originalDrug.code || "-"}
                          {line.originalDrug.desc && (
                            <span className="text-gray-500 ml-1">
                              ({line.originalDrug.desc})
                            </span>
                          )}
                        </div>
                        {line.drug && (
                          <div className="ml-2">
                            <span className="font-medium">Adjudicated:</span>{" "}
                            {line.drug.code || "-"}
                            {line.drug.desc && (
                              <span className="text-gray-500 ml-1">
                                ({line.drug.desc})
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </DetailSection>
              )}

              {/* Drug Information */}
              {hasDrug && !line.originalDrug && (
                <DetailSection title="Drug">
                  <div className="text-sm">
                    <span className="font-medium">NDC:</span>{" "}
                    <span className="font-mono">{line.drug?.code || "-"}</span>
                    {line.drug?.desc && (
                      <span className="text-gray-500 ml-1">
                        ({line.drug?.desc})
                      </span>
                    )}
                  </div>
                </DetailSection>
              )}

              {/* Supplemental Amounts */}
              {hasSupplementalAmounts && (
                <DetailSection title="Amounts">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-[11px] text-gray-500 uppercase">
                        <th className="text-left px-2 py-0.5">Qualifier</th>
                        <th className="text-left px-2 py-0.5">Type</th>
                        <th className="text-right px-2 py-0.5">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {line.supplementalAmounts?.map((amt, i) => (
                        <tr key={i} className="border-t border-gray-100">
                          <td className="px-2 py-0.5 font-mono text-gray-900">
                            {amt.qualifierCode || "-"}
                          </td>
                          <td className="px-2 py-0.5 text-gray-600 text-md">
                            {amt.type || "-"}
                          </td>
                          <td className="px-2 py-0.5 text-right font-medium">
                            {formatCurrency(amt.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </DetailSection>
              )}

              {/* Supplemental Quantities */}
              {hasSupplementalQuantities && (
                <DetailSection title="Quantities">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-[11px] text-gray-500 uppercase">
                        <th className="text-left px-2 py-0.5">Qualifier</th>
                        <th className="text-left px-2 py-0.5">Type</th>
                        <th className="text-right px-2 py-0.5">Quantity</th>
                      </tr>
                    </thead>
                    <tbody>
                      {line.supplementalQuantities?.map((qty, i) => (
                        <tr key={i} className="border-t border-gray-100">
                          <td className="px-2 py-0.5 font-mono text-gray-900">
                            {qty.qualifierCode || "-"}
                          </td>
                          <td className="px-2 py-0.5 text-gray-600 text-md">
                            {qty.type || "-"}
                          </td>
                          <td className="px-2 py-0.5 text-right font-medium">
                            {qty.quantity ?? "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </DetailSection>
              )}

              {/* Service Identifiers */}
              {hasServiceIds && (
                <DetailSection title="Service IDs">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-[11px] text-gray-500 uppercase">
                        <th className="text-left px-2 py-0.5">Qualifier</th>
                        <th className="text-left px-2 py-0.5">Type</th>
                        <th className="text-left px-2 py-0.5">ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      {line.serviceIds?.map((ref, i) => (
                        <tr key={i} className="border-t border-gray-100">
                          <td className="px-2 py-0.5 font-mono text-gray-900">
                            {ref.qualifierCode || "-"}
                          </td>
                          <td className="px-2 py-0.5 text-gray-600 text-md">
                            {ref.type || "-"}
                          </td>
                          <td className="px-2 py-0.5 font-mono">
                            {ref.identification || "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </DetailSection>
              )}

              {/* Providers */}
              {hasProviders && (
                <DetailSection title="Providers">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-[11px] text-gray-500 uppercase">
                        <th className="text-left px-2 py-0.5">Role</th>
                        <th className="text-left px-2 py-0.5">Type</th>
                        <th className="text-left px-2 py-0.5">ID</th>
                        <th className="text-left px-2 py-0.5">Name</th>
                      </tr>
                    </thead>
                    <tbody>
                      {line.providers?.map((provider, i) => (
                        <tr key={i} className="border-t border-gray-100">
                          <td className="px-2 py-0.5 text-gray-600 text-md">
                            {provider.entityRole || "-"}
                          </td>
                          <td className="px-2 py-0.5 text-gray-600 text-md">
                            {provider.entityType || "-"}
                          </td>
                          <td className="px-2 py-0.5 font-mono text-gray-900">
                            {provider.identifier || "-"}
                          </td>
                          <td className="px-2 py-0.5 text-gray-700">
                            {formatEntityName(provider)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </DetailSection>
              )}

              {/* Rendering Provider IDs */}
              {hasRenderingProviderIds && (
                <DetailSection title="Rendering">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-[11px] text-gray-500 uppercase">
                        <th className="text-left px-2 py-0.5">Qualifier</th>
                        <th className="text-left px-2 py-0.5">Type</th>
                        <th className="text-left px-2 py-0.5">ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      {line.renderingProviderIds?.map((ref, i) => (
                        <tr key={i} className="border-t border-gray-100">
                          <td className="px-2 py-0.5 font-mono text-gray-900">
                            {ref.qualifierCode || "-"}
                          </td>
                          <td className="px-2 py-0.5 text-gray-600 text-md">
                            {ref.type || "-"}
                          </td>
                          <td className="px-2 py-0.5 font-mono">
                            {ref.identification || "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </DetailSection>
              )}

              {/* Adjustments (CARC) */}
              {hasAdjustments && (
                <DetailSection
                  title="CARC"
                  id={`adjustments-section-line-${index}`}
                >
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-[11px] text-gray-500 uppercase">
                        <th className="text-left px-2 py-0.5">Group</th>
                        <th className="text-left px-2 py-0.5">Code</th>
                        <th className="text-left px-2 py-0.5">Reason</th>
                        <th className="text-right px-2 py-0.5">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {line.adjustments?.map((adj, i) => {
                        const groupCode = adj.group || "";
                        const reasonCode =
                          adj.reason?.code || adj.reasonCode || "";
                        const codeId = `adjustment-${groupCode}-${reasonCode}-line-${index}`;
                        return (
                          <tr
                            key={i}
                            id={codeId}
                            className="border-t border-gray-100"
                          >
                            <td className="px-2 py-0.5 text-gray-600">
                              {adj.group}
                            </td>
                            <td className="px-2 py-0.5 font-mono text-gray-900">
                              {reasonCode}
                            </td>
                            <td className="px-2 py-0.5 text-gray-600 text-md">
                              {adj.reason?.desc || "-"}
                            </td>
                            <td className="px-2 py-0.5 text-right text-red-600">
                              {formatCurrency(adj.amount)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </DetailSection>
              )}

              {/* Remark Codes (RARC) */}
              {hasRemarks && (
                <DetailSection
                  title="RARC"
                  id={`remarks-section-line-${index}`}
                >
                  {line.remarks && line.remarks.length > 0 ? (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-[11px] text-gray-500 uppercase">
                          <th className="text-left px-2 py-0.5">Type</th>
                          <th className="text-left px-2 py-0.5">Code</th>
                          <th className="text-left px-2 py-0.5">Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {line.remarks.map((remark, i) => {
                          const remarkCode = remark.code || "";
                          const codeId = `remark-${remarkCode}-line-${index}`;
                          return (
                            <tr
                              key={i}
                              id={codeId}
                              className="border-t border-gray-100"
                            >
                              <td className="px-2 py-0.5 text-gray-600 text-md">
                                {remark.subType || "-"}
                              </td>
                              <td className="px-2 py-0.5 font-mono text-gray-900">
                                {remarkCode}
                              </td>
                              <td className="px-2 py-0.5 text-gray-600 text-md">
                                {remark.desc || remark.instructionDesc || "-"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : line.remarkCodes && line.remarkCodes.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {line.remarkCodes.map((code, i) => {
                        const codeId = `remark-${code}-line-${index}`;
                        return (
                          <span
                            key={i}
                            id={codeId}
                            className="px-1.5 py-0.5 bg-yellow-100 text-yellow-800 rounded text-sm font-mono"
                          >
                            {code}
                          </span>
                        );
                      })}
                    </div>
                  ) : null}
                </DetailSection>
              )}

              {hasLineDrg && lineDrg && (
                <DetailSection title="DRG" id={`drg-section-line-${index}`}>
                  <div className="text-sm text-gray-700">
                    <span className="font-mono font-medium text-gray-900">
                      {lineDrg.code}
                    </span>
                    {lineDrg.subType && (
                      <span className="ml-2 text-gray-500">
                        ({lineDrg.subType})
                      </span>
                    )}
                    {lineDrg.description && (
                      <div className="mt-1 text-gray-600">
                        {lineDrg.description}
                      </div>
                    )}
                  </div>
                </DetailSection>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export default PaymentServiceLineRow;
